import React, { useEffect, useMemo, useState } from 'react';
import {
    useTable,
    useBlockLayout,
    Cell,
    HeaderGroup,
    useSortBy,
    useRowSelect,
} from 'react-table';
import cx from 'classnames';
import _isEqual from 'lodash/isEqual';

import {
    FileSizeCell,
    TableHeader,
    TableHeaderGroup,
    TruncatedListCell,
    TruncatedTwoRowCell,
} from '@trqk/components';

import { rowHeight, defaultColumnWidth } from './constants';
import { DownloadLinkCell } from './components';
import { getLogEventErrorMessage } from '../../../../../utils';

interface Props {
    height: number;
    failedIngestions: IngestionFailure[];
    setSelectedRowIds(selectedRowIds: { [key: string]: boolean }): void;
}

/* eslint-disable react/jsx-props-no-spreading */

/**
 * Renders a table of details for failed statement uploads.
 */
export const FailedIngestionsTable = ({
    height,
    setSelectedRowIds,
    failedIngestions,
}: Props): JSX.Element => {
    const [data, setData] = useState(failedIngestions);

    useEffect((): void => {
        if (!_isEqual(failedIngestions, data)) {
            setData(failedIngestions);
        }
    }, [failedIngestions, data, setData]);

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const columns = useMemo(
        () => [
            {
                id: 'filename',
                accessor: ({ filename }: IngestionFailure): Maybe<string> =>
                    filename,
                Cell: TruncatedTwoRowCell,
                headerLabel: 'File Name',
                Header: TableHeader,
                width: 400,
            },
            {
                id: 'size',
                accessor: ({ fileSizeBytes }: IngestionFailure): number =>
                    fileSizeBytes,
                Cell: FileSizeCell,
                headerLabel: 'Size',
                Header: TableHeader,
                width: 70,
            },
            {
                id: 'alert',
                accessor: ({
                    addStatementFailure,
                    statementView,
                    logEventFailures,
                }: IngestionFailure): string[] => {
                    let errors = [] as string[];

                    if (addStatementFailure !== null) {
                        // If the mutation `addStatementToTrqkUser` failed, get error messaging from
                        // that failure
                        const { error: addStatementError } =
                            addStatementFailure;

                        if (addStatementError) {
                            errors = [addStatementError];
                        }
                    } else if (statementView !== null) {
                        // Otherwise, get error messaging from the statementView that was created by
                        // `addStatementToTrqkUser`, combined with any failureMessages generated by
                        //  log events
                        errors = logEventFailures.map(
                            (event: LogEvent): string =>
                                getLogEventErrorMessage(event),
                        );

                        const { status_message } = statementView;

                        if (status_message) {
                            errors = [status_message, ...errors];
                        }
                    }

                    return errors;
                },
                Cell: TruncatedListCell,
                headerLabel: 'Alert',
                Header: TableHeader,
                width: 300,
            },
            {
                id: 'download',
                accessor: ({ statementUrl }: IngestionFailure): string =>
                    statementUrl,
                Cell: DownloadLinkCell,
                headerLabel: 'Statement CSV',
                Header: TableHeader,
                width: 130,
                disableSortBy: true,
            },
        ],
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state: { selectedRowIds },
    } = useTable<IngestionFailure>(
        {
            columns,
            data,
            defaultColumn,
            initialState: {
                sortBy: [
                    {
                        id: 'filename',
                        desc: false,
                    },
                ],
            },
            autoResetSelectedRows: false,
        },
        useBlockLayout,
        useSortBy,
        useRowSelect,
    );

    /* END table management logic */

    // Update selectedRowIds in UploadAudio when selections are changed in the table
    useEffect(() => {
        setSelectedRowIds(selectedRowIds);
    }, [setSelectedRowIds, selectedRowIds]);

    const renderedRows = useMemo(
        () =>
            rows.map((row, index) => {
                const ariaRowIndex = index + 1;
                const evenRow = ariaRowIndex % 2 === 0;

                prepareRow(row);
                const rowProps = row.getRowProps();

                const { key: rowKey, role, style } = rowProps;
                const classNames = cx('tr relative flex items-center px-2', {
                    'even-row': evenRow,
                });

                return (
                    <div
                        key={rowKey}
                        role={role}
                        style={{
                            ...style,
                            height: rowHeight,
                        }}
                        aria-rowindex={ariaRowIndex}
                        className={classNames}
                    >
                        {row.cells.map((cell: Cell<IngestionFailure>) => {
                            const { key: cellKey, ...restOfCellProps } =
                                cell.getCellProps();

                            return (
                                <div
                                    key={cellKey}
                                    {...restOfCellProps}
                                    className="td text-sm"
                                >
                                    {cell.render('Cell')}
                                </div>
                            );
                        })}
                    </div>
                );
            }),
        // We include selectedRowIds here because it allows checkbox rendering to update correctly,
        // even though it doesn't look like a necessary dependency for useEffect.
        // eslint-disable-next-line
        [prepareRow, rows, selectedRowIds],
    );

    return (
        <div {...getTableProps()} className="trqk-table font-normal">
            <div>
                {headerGroups.map(
                    (
                        headerGroup: HeaderGroup<IngestionFailure>,
                    ): JSX.Element => {
                        const { key: headerGroupKey } =
                            headerGroup.getHeaderGroupProps();

                        return (
                            <TableHeaderGroup<IngestionFailure>
                                key={headerGroupKey}
                                headerGroup={headerGroup}
                            />
                        );
                    },
                )}
            </div>
            <div
                {...getTableBodyProps()}
                style={{ height }}
                className="overflow-y-auto respond-to-mouse"
            >
                {renderedRows}
            </div>
        </div>
    );
};
